import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  useGetExposureQuery,
  usePostExposureVisitMutation,
  useAddExposureStudentMutation,
  usePostExposureUploadsMutation,
} from '../../student.service';
import {
  STUDENT_JSON_BASE,
  DISTRICT_MASTER_URL,
  COLLEGE_LIST_URL,
} from './constants';
import { CollegeItem, DropdownOption, StudentRow, TeacherItem } from './types';

// Holds all data/state, master-list loading, and derived table rows for the
// Student Exposure Visit page. UI handlers live in useExposureActions.
export function useExposureData() {
  const s = useSelector((state: RootState) => state.session);
  const schoolId = s.schoolId ?? 0;

  // user_ids removed optimistically (before the refetch confirms it).
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [districtList, setDistrictList] = useState<DropdownOption[]>([]);
  const [collegeOptions, setCollegeOptions] = useState<DropdownOption[]>([]);
  const [collegeMaster, setCollegeMaster] = useState<CollegeItem[]>([]);
  const [classGroupDetails] = useState<any[]>([]);
  const [allClassSectionData, setAllClassSectionData] = useState<any[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<DropdownOption[]>([]);

  const { data: exposureData, refetch: refetchExposure } = useGetExposureQuery(
    { schoolId },
    { skip: !s.loaded || !schoolId }
  );
  const [postExposureVisit] = usePostExposureVisitMutation();
  const [addExposureStudent, { isLoading: isAddingStudents }] =
    useAddExposureStudentMutation();
  const [postExposureUploads, { isLoading: isUploading }] =
    usePostExposureUploadsMutation();

  const getGroupName = useMemo(
    () =>
      (stu: any): string => {
        const match = classGroupDetails.find(
          (item: any) =>
            item.class_id == stu.class_studying_id &&
            item.section == stu.class_section &&
            item.group_id == stu.group_code_id
        );
        return match ? match.group_name : '';
      },
    [classGroupDetails]
  );

  // Load district master (CloudFront)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(DISTRICT_MASTER_URL);
        const data = await res.json();
        setDistrictList(
          (data?.district ?? []).map((e: any) => ({
            label: e.district_name,
            value: e.disid,
          }))
        );
      } catch {
        /* leave district list empty */
      }
    })();
  }, []);

  // Load college list (CloudFront) + append static "Others" option
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(COLLEGE_LIST_URL);
        const data: CollegeItem[] = await res.json();
        // The list already includes an "Others" entry (college_id 40000);
        // no static option is appended.
        setCollegeMaster(data);
        setCollegeOptions(
          data.map((e) => ({ value: e.college_id, label: e.college_name }))
        );
      } catch {
        /* leave college list empty */
      }
    })();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${STUDENT_JSON_BASE}${schoolId}_1.json`),
          fetch(`${STUDENT_JSON_BASE}${schoolId}_2.json`),
        ]);
        const d1 = await r1.json().catch(() => ({}));
        const d2 = await r2.json().catch(() => ({}));

        // Students: _1.json
        const list: any[] = d1?.studentlist ?? [];
        setAllClassSectionData(list.filter((e) => e.class_studying_id == 12));

        // Teachers: _1 + _2 merged, duplicate teacher_id remove
        const merged: TeacherItem[] = [
          ...(d1?.teacherlist ?? []),
          ...(d2?.teacherlist ?? []),
        ];
        const unique = Array.from(
          new Map(merged.map((t) => [t.teacher_id, t])).values()
        );
        setTeacherOptions(
          unique.map((t) => ({
            value: t.teacher_id,
            label: `${t.teacher_name} – ${t.Designation ?? ''}`,
          }))
        );
      } catch {
        /* leave lists empty */
      }
    })();
  }, [schoolId]);

  // Derive the table from the GET API result enriched by the attendance JSON.
  // Derived with useMemo (no setState-in-effect); optimistic deletes are
  // applied via the removedIds filter below.
  const studentJsonData = useMemo<StudentRow[]>(() => {
    const saved: any[] = exposureData?.result ?? [];
    const rows: StudentRow[] = saved.map((match: any) => {
      const stu =
        allClassSectionData.find((e: any) => e.user_id == match.StudId) ?? {};
      return {
        ...stu,
        ...match,
        user_id: stu.user_id ?? match.StudId,
        name: match.name ?? stu.name,
        class_studying_id: match.class_studying_id ?? stu.class_studying_id,
        class_section: match.class_section ?? stu.class_section ?? stu.section,
        group_name: getGroupName(stu),
        visit: '1',
        IndxID: match.IndxID,
        ExpotStatus: match.ExpotStatus,
        date_visit: match.date_visit ?? '',
        ClgId: match.ClgId ?? '',
        district: match.district,
        hod_name: match.hod_name,
        instititute_name: match.instititute_name ?? '',
        OtherClgId: match.instititute_name ?? '',
        No_reason: match.NoReason ?? '',
        Other_Reason: match.OtherReason ?? '',
        photo_upload: match.photo_upload ?? '',
        video_upload: match.video_upload ?? '',
      } as StudentRow;
    });
    rows.sort((a, b) => Number(b.IndxID || 0) - Number(a.IndxID || 0));
    return rows.filter((r) => !removedIds.has(String(r.user_id)));
  }, [exposureData, allClassSectionData, getGroupName, removedIds]);

  // Optimistically hide a removed student until the next refetch.
  const removeStudentLocally = (userId: string | number) =>
    setRemovedIds((prev) => new Set(prev).add(String(userId)));

  const savedUploads = useMemo(() => {
    const data: any = exposureData ?? {};
    const first: any = data?.result?.[0] ?? {};
    return {
      photo: data.photo_upload ?? first.photo_upload ?? '',
      video: data.video_upload ?? first.video_upload ?? '',
    };
  }, [exposureData]);

  return {
    schoolId,
    session: s,
    studentJsonData,
    removeStudentLocally,
    districtList,
    setDistrictList,
    collegeOptions,
    setCollegeOptions,
    collegeMaster,
    allClassSectionData,
    getGroupName,
    exposureData,
    refetchExposure,
    postExposureVisit,
    addExposureStudent,
    isAddingStudents,
    teacherOptions,
    postExposureUploads,
    isUploading,
    savedUploads,
  };
}
